import { Component, computed, inject, input, output, signal, viewChildren } from '@angular/core';
import { NgIcon } from '@ng-icons/core';
import {
  Button,
  Skeleton,
  PopoverComponent,
  PopoverDirective,
  ClickOutsideDirective,
  SimpleDialog,
  NotificationService,
  Checkbox,
  TooltipDirective,
} from 'excellence-ui';
import {
  DocumentsFilter,
  DocumentsPage,
  DocumentState,
  DocumentUpdateChanges,
} from '../../stores/documents.store';
import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { ErrorPage } from '../../../../shared/components/error-page/error-page';
import { Router } from '@angular/router';
import { InViewportDirective } from '../../../../shared/directives/in-viewport';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { DocumentFileTypePipe } from '../../pipes/document-file-type.pipe';
import { FileSizePipe } from '../../pipes/file-size.pipe';
import { DocumentTableRowContextmenu } from '../document-table-row-contextmenu/document-table-row-contextmenu';
import { DocumentIcon } from '../document-icon/document-icon';
import { DocumentEditDialog } from '../../dialogs/document-edit-dialog/document-edit-dialog';
import { DocumentPreviewDialog } from '../../dialogs/document-preview-dialog/document-preview-dialog';
import { DocumentFilenamePipe } from '../../pipes/document-filename-pipe';
import { DocumentShareDialog } from '../../dialogs/document-share-dialog/document-share-dialog';
import { HasPermissionDirective } from '../../../organizations/directives/has-permission';

@Component({
  selector: 'app-documents-table',
  imports: [
    NgIcon,
    Button,
    Skeleton,
    NgTemplateOutlet,
    ErrorPage,
    DatePipe,
    InViewportDirective,
    NgClass,
    TranslocoPipe,
    DocumentFileTypePipe,
    FileSizePipe,
    PopoverComponent,
    PopoverDirective,
    DocumentTableRowContextmenu,
    ClickOutsideDirective,
    DocumentIcon,
    SimpleDialog,
    DocumentEditDialog,
    DocumentPreviewDialog,
    DocumentFilenamePipe,
    DocumentShareDialog,
    Checkbox,
    HasPermissionDirective,
    TooltipDirective,
  ],
  templateUrl: './documents-table.html',
})
export class DocumentsTable {
  protected router = inject(Router);
  private notificationService = inject(NotificationService);
  private translocoService = inject(TranslocoService);

  documents = input.required<DocumentsPage[]>();
  organizationId = input.required<string>();
  showLoadMorePlaceholders = input.required<boolean>();
  limit = input.required<number>();

  orderBy = input.required<DocumentsFilter['data']['orderBy']>();
  orderDirection = input.required<DocumentsFilter['data']['orderDirection']>();

  changeOrder = output<{
    orderBy: Exclude<DocumentsFilter['data']['orderBy'], undefined>;
    orderDirection: Exclude<DocumentsFilter['data']['orderDirection'], undefined>;
  }>();

  deleteDocuments = output<string[]>();

  selectedRows = signal<Record<string, boolean>>({});
  protected allSelected = computed<'OFF' | 'CHECKED' | 'INDETERMINATE'>(() => {
    const rows = this.documents().flatMap((page) =>
      page.documents.filter((d) => !d.isMarkedForDeletion),
    );
    if (rows.length === 0) {
      return 'OFF';
    }
    const selectedRows = this.selectedRows();
    const unselectedRows = rows.filter((row) => selectedRows[row.data.id] !== true);
    if (unselectedRows.length === 0) {
      return 'CHECKED';
    }
    if (unselectedRows.length === rows.length) {
      return 'OFF';
    }
    return 'INDETERMINATE';
  });

  private contextMenus = viewChildren<PopoverComponent>('optionsContextmenu');

  protected areAllPagesLoading = computed(() => this.documents().every((page) => page.isLoading));

  protected placeholders = computed(() => [...Array(this.limit())]);

  /**
   * helper for dialogs
   */
  protected selectedDocument = signal<DocumentState | null>(null);

  protected onClickHeader(orderBy: DocumentsFilter['data']['orderBy']) {
    if (!orderBy) {
      return;
    }
    if (this.orderBy() === orderBy) {
      this.changeOrder.emit({
        orderBy,
        orderDirection: this.orderDirection() === 'asc' ? 'desc' : 'asc',
      });
    } else {
      this.changeOrder.emit({
        orderBy,
        orderDirection: 'asc',
      });
    }
  }

  dismissContextMenus() {
    this.contextMenus().forEach((cm) => cm.hide());
  }

  protected onClickPreviewButton(document: DocumentState, dialog: SimpleDialog) {
    this.selectedDocument.set(document);
    dialog.showModal();
  }

  protected openSelectedDocumentDialog(
    document: DocumentState,
    contextmenu: PopoverComponent,
    dialog: SimpleDialog,
  ) {
    this.selectedDocument.set(document);
    contextmenu.hide();
    dialog.showModal();
  }

  protected onEditSuccess(changes: DocumentUpdateChanges, dialog: SimpleDialog) {
    dialog.close();
    this.selectedDocument.set(null);
    let subtitle = this.translocoService.translate(
      'cockpit.documents.table.editSuccess.subtitleBoth',
    );
    if (!changes.fileType && changes.filename) {
      subtitle = this.translocoService.translate(
        'cockpit.documents.table.editSuccess.subtitleTitle',
      );
    } else if (changes.fileType && !changes.filename) {
      subtitle = this.translocoService.translate(
        'cockpit.documents.table.editSuccess.subtitleCategory',
      );
    }
    this.notificationService.showSuccess({
      title: this.translocoService.translate('cockpit.documents.table.editSuccess.title'),
      subtitle,
    });
  }

  protected onSelectRowChange(id: string, checked: boolean) {
    this.selectedRows.update((prev) => ({ ...prev, [id]: checked }));
  }

  protected onSelectAllChange(checked: boolean) {
    const rows = this.documents().flatMap((page) => page.documents);
    if (!checked) {
      this.selectedRows.set({});
    } else {
      const selectedRows: Record<string, boolean> = {};
      rows
        .filter((row) => !row.isMarkedForDeletion)
        .forEach((row) => (selectedRows[row.data.id] = true));
      this.selectedRows.set(selectedRows);
    }
  }

  protected async onClickDelete(document: DocumentState, popover: PopoverComponent) {
    popover.hide();
    this.deleteDocuments.emit([document.data.id]);
  }

  protected async onClickDeleteSelected() {
    const rows = this.documents().flatMap((page) => page.documents);
    const selectedRows = this.selectedRows();
    const selected = rows.filter((row) => selectedRows[row.data.id] === true);
    this.deleteDocuments.emit(selected.map((document) => document.data.id));
  }
}
